import { useCallback, useEffect, useRef, useState, type FocusEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  MASTER_COLUMN_KEY_PATTERN,
  canonicalMasterValue,
  type MasterAttrValue,
  type MasterImportMode,
  type MasterImportResult,
  type MasterItem,
  type MasterList,
  type MasterTemplate,
  type MasterTemplateColumn,
  type MasterTemplateInferResult,
  type NormalizedTxn,
} from '@kkp-fraud/shared';
import { ApiError, COOKIE_MODE, api, type ApiSettings } from '../api';
import { useShell } from '../components/AppLayout';
import { Dialog } from '../components/Dialog';
import { Field, FieldGroup, useFieldErrors } from '../components/Field';
import { Kv } from '../components/Kv';
import { When } from '../components/When';
import { actorLabel } from '../format';
import { ActionMenu } from '../components/ActionMenu';
import { PageHead, SubTabs, Tip } from '../components/PageHead';
import { downloadBlob } from '../csv';
import { DEMO, isDemoMaster, isDemoTemplate } from '../demo';
import { t } from '../i18n';
import {
  COLUMN_TYPES,
  TEMPLATE_MAX_LABEL,
  assignableTemplates,
  attrInput,
  fieldsFor,
  keyMatches,
  masterFormProblems,
  parseAttr,
  parseAttrs,
  templateFormOf,
  templateFormProblems,
  type MasterForm,
  type MasterFormProblem,
  type TemplateForm,
  type TemplateFormProblem,
} from '../masters';
import { canEdit } from '../session';

/**
 * Master Management — named lists (a merchant watch list, a card block list, a group of MCCs)
 * that a rule checks a transaction against by id. The lists live in the admin service
 * (`/api/masters`), not in the rule document: they are edited and imported directly, each
 * change is versioned and audited there, and there is no draft to publish.
 *
 * `/masters` is the list of lists (and, `?tab=templates`, the templates), `/masters/:id` one
 * list and its values. A Viewer reads and exports; an Editor or Approver also creates, edits,
 * deletes, adds, removes and imports.
 *
 * R11 — a list is made from a TEMPLATE: the template fixes the key type, the one rule field the
 * list binds to (the clause editor offers a list only on that field), an optional key pattern
 * and the extra columns each value may carry. A list from before templates shows "no
 * template"; it stays usable and can be assigned a template whose key type and field match.
 */
export function MasterScreen() {
  const { id } = useParams();
  return id ? <MasterDetail key={id} id={id} /> : <MasterHome />;
}

/** Writes need the admin service (cookie mode) and a writing role; a demo list is never written. */
function useMayWrite(): (id?: string) => boolean {
  const { session } = useShell();
  return (id?: string) => COOKIE_MODE && canEdit(session) && !(id !== undefined && isDemoMaster(id));
}

function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'master_in_use') return t('master.delete.in_use', { rules: (e.rules ?? []).join(', ') });
    if (e.code === 'template_in_use') {
      // QA-A-05: a column cannot become required while lists use the template.
      if (e.reason === 'required_column') return t('templates.in_use.required', { lists: (e.lists ?? []).join(', '), columns: (e.columns ?? []).join(', ') });
      return e.columns?.length
        ? t('templates.in_use.columns', { lists: (e.lists ?? []).join(', '), columns: e.columns.join(', ') })
        : t('templates.in_use', { lists: (e.lists ?? []).join(', ') });
    }
    if (e.code === 'template_conflict') return t('templates.conflict');
    // QA-A-16: a file with no values — least of all a Replace, which would empty the list.
    if (e.code === 'empty_file') return t('master.import.empty');
    if (e.problems?.length) return e.problems.map((p) => `${p.field}: ${p.message}`).join('; ');
  }
  return (e as Error).message;
}

async function exportList(settings: ApiSettings, id: string, onError: (m: string) => void, format: 'csv' | 'xlsx' = 'csv'): Promise<void> {
  try {
    downloadBlob(`${id}.${format}`, await (format === 'xlsx' ? api.masters.exportXlsx(settings, id) : api.masters.exportCsv(settings, id)));
  } catch (e) {
    onError(errorText(e));
  }
}

/** A template's sample workbook: the header row the import reads and example rows valid for the template. */
async function downloadSample(settings: ApiSettings, templateId: string, onError: (m: string) => void): Promise<void> {
  try {
    downloadBlob(`${templateId}.sample.xlsx`, await api.masters.templates.sampleXlsx(settings, templateId));
  } catch (e) {
    onError(errorText(e));
  }
}

/** Templates, or `[]` when they cannot be read (the lists still show; a list's template reads as its id). */
function useTemplates(): { templates: MasterTemplate[] | null; reloadTemplates: () => Promise<void>; templateError: string | null } {
  const { settings } = useShell();
  const [templates, setTemplates] = useState<MasterTemplate[] | null>(null);
  const [templateError, setError] = useState<string | null>(null);
  const reloadTemplates = useCallback(async () => {
    try {
      const got = await api.masters.templates.list(settings);
      setTemplates(Array.isArray(got) ? got : []);
      setError(null);
    } catch (e) {
      setTemplates([]);
      setError(errorText(e));
    }
  }, [settings]);
  useEffect(() => {
    void reloadTemplates();
  }, [reloadTemplates]);
  return { templates, reloadTemplates, templateError };
}

/** A column's value as a cell shows it. */
function attrText(v: MasterAttrValue | undefined): string {
  if (v === undefined) return '';
  if (typeof v === 'boolean') return t(v ? 'templates.bool.true' : 'templates.bool.false');
  return String(v);
}

function TemplateChip({ list }: { list: MasterList }) {
  return list.templateId ? (
    <span className="mono small">{list.templateId}</span>
  ) : (
    <span className="chip mute" data-testid={`master-notemplate-${list.id}`}>
      {t('templates.none.chip')}
    </span>
  );
}

// ---- the home: lists | templates -------------------------------------------------------------

function MasterHome() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'templates' ? 'templates' : 'lists';
  return (
    <>
      <PageHead title={t('master.title')} lead={t('master.lead')} />

      {!COOKIE_MODE && !DEMO && (
        <div className="banner warn" role="status" data-testid="master-unavailable" style={{ marginBottom: 12 }}>
          {t('master.unavailable')}
        </div>
      )}

      <SubTabs
        label={t('templates.tabs.a11y')}
        active={tab}
        onSelect={(next) => setParams(next === 'templates' ? { tab: 'templates' } : {}, { replace: true })}
        tabs={[
          { id: 'lists', label: t('templates.tab.lists') },
          { id: 'templates', label: t('templates.tab.templates') },
        ]}
      />
      {tab === 'templates' ? <TemplatesTab /> : <MasterLists />}
    </>
  );
}

// ---- the list of lists ---------------------------------------------------------------

function MasterLists() {
  const { settings, showToast } = useShell();
  const navigate = useNavigate();
  const mayWrite = useMayWrite();
  const { templates } = useTemplates();
  const [lists, setLists] = useState<MasterList[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<MasterList | null>(null);
  const [deleting, setDeleting] = useState<MasterList | null>(null);
  const [assigning, setAssigning] = useState<MasterList | null>(null);

  const reload = useCallback(async () => {
    try {
      const got = await api.masters.list(settings);
      setLists(Array.isArray(got) ? got : []);
      setError(null);
    } catch (e) {
      setLists([]);
      setError(errorText(e));
    }
  }, [settings]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <>
      {error && (
        <div className="banner error" role="status" data-testid="master-error" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}

      <section className="card">
        <header className="card-head">
          <h2>{t('master.lists.title')}</h2>
          {lists && (
            <span className="chip mute" data-testid="master-count">
              {t('master.lists.count', { n: lists.length })}
            </span>
          )}
          <span className="spacer" />
          {mayWrite() && (
            <button type="button" className="btn small" data-testid="master-new" onClick={() => setCreating(true)}>
              {t('master.new.action')}
            </button>
          )}
        </header>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 240 }}>{t('master.col.id')}</th>
                <th>{t('master.col.name')}</th>
                <th style={{ width: 140 }}>{t('templates.col.template')}</th>
                <th style={{ width: 150 }}>{t('master.col.field')}</th>
                <th className="right" style={{ width: 90 }}>
                  {t('master.col.items')}
                </th>
                <th className="right" style={{ width: 80 }}>
                  {t('master.col.version')}
                </th>
                <th style={{ width: 260 }}>{t('master.col.updated')}</th>
                <th style={{ width: 44 }} aria-label={t('master.col.actions')} />
              </tr>
            </thead>
            <tbody>
              {lists === null && (
                <tr>
                  <td colSpan={8} className="empty">
                    {t('master.loading')}
                  </td>
                </tr>
              )}
              {lists?.length === 0 && (
                <tr>
                  <td colSpan={8} className="empty" role="status" data-testid="master-empty">
                    {t('master.lists.empty')}
                  </td>
                </tr>
              )}
              {lists?.map((l) => {
                const writable = mayWrite(l.id);
                return (
                  <tr
                    key={l.id}
                    className="clickable"
                    data-testid={`master-row-${l.id}`}
                    onClick={() => navigate(`/masters/${encodeURIComponent(l.id)}`)}
                  >
                    <td className="mono">
                      {l.id}
                      {isDemoMaster(l.id) && (
                        <span className="chip warn" style={{ marginLeft: 6 }}>
                          {t('master.demo.chip')}
                        </span>
                      )}
                    </td>
                    <td>
                      {/* UX review P1-5: the name is the row's keyboard way in. */}
                      <div>
                        <Link
                          className="rowlink"
                          to={`/masters/${encodeURIComponent(l.id)}`}
                          data-testid={`master-open-${l.id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {l.name || l.id}
                        </Link>
                      </div>
                      {l.description && <div className="small muted">{l.description}</div>}
                    </td>
                    <td>
                      <TemplateChip list={l} />
                    </td>
                    <td className="mono small">{l.field ?? '—'}</td>
                    <td className="right" data-testid={`master-items-${l.id}`}>
                      {l.itemCount.toLocaleString('en-US')}
                    </td>
                    <td className="right">{l.version}</td>
                    <td className="small">
                      <div>
                        <When iso={l.updatedUtc} />
                      </div>
                      <div className="muted" title={l.updatedBy}>
                        {actorLabel(l.updatedBy)}
                      </div>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <ActionMenu
                        variant="row"
                        label={t('master.row.menu.a11y', { id: l.id })}
                        items={[
                          ...(writable ? [{ label: t('master.menu.edit'), onSelect: () => setEditing(l) }] : []),
                          ...(writable && !l.templateId ? [{ label: t('templates.assign.action'), onSelect: () => setAssigning(l) }] : []),
                          { label: t('master.export.action'), onSelect: () => void exportList(settings, l.id, (m) => showToast(m, 'error')) },
                          ...(writable ? [{ label: t('master.menu.delete'), onSelect: () => setDeleting(l), separated: true }] : []),
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {creating && (
        <ListDialog
          templates={templates ?? []}
          onClose={() => setCreating(false)}
          onSaved={(l) => {
            setCreating(false);
            showToast(t('master.created.toast', { id: l.id }));
            void reload();
          }}
        />
      )}
      {editing && (
        <ListDialog
          initial={editing}
          templates={templates ?? []}
          onClose={() => setEditing(null)}
          onSaved={(l) => {
            setEditing(null);
            showToast(t('master.saved.toast', { id: l.id }));
            void reload();
          }}
        />
      )}
      {deleting && (
        <DeleteDialog
          list={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            showToast(t('master.deleted.toast', { id: deleting.id }));
            setDeleting(null);
            void reload();
          }}
        />
      )}
      {assigning && (
        <AssignDialog
          list={assigning}
          templates={templates ?? []}
          onClose={() => setAssigning(null)}
          onDone={(l) => {
            setAssigning(null);
            showToast(t('templates.assigned.toast', { id: l.id, template: l.templateId ?? '' }));
            void reload();
          }}
        />
      )}
    </>
  );
}

// ---- dialogs ---------------------------------------------------------------------------

function Problem({ text, testid }: { text: string | null; testid: string }) {
  if (!text) return null;
  return (
    <div className="why-missing" role="alert" data-testid={testid} style={{ margin: 0 }}>
      {text}
    </div>
  );
}

const PROBLEM_KEY: Record<MasterFormProblem, string> = {
  id: 'master.problem.id',
  name: 'master.problem.name',
  name_long: 'master.problem.name_long',
  description_long: 'master.problem.description_long',
};

/** One template's key type, field and columns, as a short line under its choice. */
function TemplateSummary({ template, testid }: { template: MasterTemplate; testid?: string }) {
  return (
    <div className="small muted" data-testid={testid}>
      {t('templates.summary', { keyType: t(`master.type.${template.keyType}`), field: template.field })}
      {' · '}
      {template.columns.length > 0
        ? t('templates.summary.columns', { columns: template.columns.map((c) => c.label).join(', ') })
        : t('templates.columns.empty')}
    </div>
  );
}

/**
 * New list (no `initial`) or edit one. A new list is made from a template, which decides its
 * value type and field. Editing a list keeps its id and value type; its field is the
 * template's, and only a list with no template (made before templates) still picks its own.
 */
function ListDialog({
  initial,
  templates,
  onClose,
  onSaved,
}: {
  initial?: MasterList;
  templates: readonly MasterTemplate[];
  onClose: () => void;
  onSaved: (l: MasterList) => void;
}) {
  const { settings } = useShell();
  const [form, setForm] = useState<MasterForm>({
    id: initial?.id ?? '',
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    valueType: initial?.valueType ?? 'string',
    field: initial?.field ?? '',
  });
  const [templateId, setTemplateId] = useState('');
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const isNew = initial === undefined;
  const legacy = !isNew && !initial.templateId;
  const template = templates.find((x) => x.id === (isNew ? templateId : initial.templateId));
  const problems = masterFormProblems(form, isNew);
  const patch = (p: Partial<MasterForm>) => setForm((f) => ({ ...f, ...p }));
  const fields = fieldsFor(form.valueType);
  // UX review P2-5: a field's problem shows once the field was left or Create was pressed —
  // never on a dialog that has just opened.
  const errors = useFieldErrors();
  const nameProblem = problems.find((p) => p === 'name' || p === 'name_long');
  const blocked = problems.length > 0 || (isNew && !template);

  const save = async () => {
    if (!errors.attempt(!blocked, document.querySelector('[data-testid="master-list-dialog"]'))) return;
    setBusy(true);
    setServerError(null);
    try {
      const description = form.description.trim();
      const saved = isNew
        ? await api.masters.create(settings, {
            id: form.id.trim(),
            name: form.name.trim(),
            templateId,
            ...(description ? { description } : {}),
          })
        : await api.masters.update(settings, initial.id, {
            name: form.name.trim(),
            description: description || null,
            // Only a list with no template sets its own field; any other takes its template's.
            ...(legacy ? { field: form.field || null } : {}),
          });
      onSaved(saved);
    } catch (e) {
      setServerError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      busy={busy}
      title={isNew ? t('master.create.title') : t('master.edit.title', { id: initial.id })}
      testid="master-list-dialog"
      onClose={onClose}
      foot={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t('btn.cancel.label')}
          </button>
          <button type="button" className="btn primary" data-testid="master-save" disabled={busy} onClick={() => void save()}>
            {isNew ? t('master.create.action') : t('master.save.action')}
          </button>
        </>
      }
    >
      <Field
        label={t('master.form.id')}
        required={isNew}
        hint={isNew ? t('master.form.id.tip') : undefined}
        name="id"
        form={errors}
        error={errors.show('id', problems.includes('id') && t('master.problem.id'))}
      >
        <input
          type="text"
          className="mono-input"
          value={form.id}
          disabled={!isNew}
          spellCheck={false}
          onChange={(e) => patch({ id: e.target.value.toUpperCase() })}
        />
      </Field>
      {isNew ? (
        <>
          <Field
            label={t('templates.col.template')}
            required
            hint={t('templates.list.form.tip')}
            name="template"
            form={errors}
            error={errors.show('template', !template && t('templates.problem.template'))}
          >
            <select data-testid="master-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              <option value="">{t('templates.list.form.choose')}</option>
              {templates.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.id} · {x.name}
                </option>
              ))}
            </select>
          </Field>
          {template && <TemplateSummary template={template} testid="master-template-summary" />}
        </>
      ) : legacy ? (
        <div className="form-grid">
          <Field label={t('master.form.valueType')}>
            <select value={form.valueType} disabled>
              <option value="string">{t('master.type.string')}</option>
              <option value="number">{t('master.type.number')}</option>
            </select>
          </Field>
          <Field label={t('master.form.field')}>
            <select value={form.field} onChange={(e) => patch({ field: e.target.value as keyof NormalizedTxn | '' })}>
              <option value="">{t('master.field.none')}</option>
              {fields.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </Field>
        </div>
      ) : (
        <div className="field">
          <span>{t('templates.col.template')}</span>
          <span className="mono" data-testid="master-template-fixed">
            {initial.templateId}
          </span>
          {template && <TemplateSummary template={template} />}
        </div>
      )}
      <Field
        label={t('master.form.name')}
        required
        name="name"
        form={errors}
        error={errors.show('name', nameProblem && t(PROBLEM_KEY[nameProblem]))}
      >
        <input type="text" value={form.name} onChange={(e) => patch({ name: e.target.value })} />
      </Field>
      <Field
        label={t('master.form.description')}
        name="description"
        form={errors}
        error={errors.show('description', problems.includes('description_long') && t('master.problem.description_long'))}
      >
        <input type="text" value={form.description} onChange={(e) => patch({ description: e.target.value })} />
      </Field>
      <Problem testid="master-form-error" text={serverError} />
    </Dialog>
  );
}

/** A list with no template takes one whose key type and field are its own — once. */
function AssignDialog({
  list,
  templates,
  onClose,
  onDone,
}: {
  list: MasterList;
  templates: readonly MasterTemplate[];
  onClose: () => void;
  onDone: (l: MasterList) => void;
}) {
  const { settings } = useShell();
  const offered = assignableTemplates(list, templates);
  const [templateId, setTemplateId] = useState(offered[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const assign = async () => {
    setBusy(true);
    setError(null);
    try {
      onDone(await api.masters.assignTemplate(settings, list.id, templateId));
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };
  const chosen = offered.find((x) => x.id === templateId);
  return (
    <Dialog
      busy={busy}
      title={t('templates.assign.title', { id: list.id })}
      testid="master-assign-dialog"
      onClose={onClose}
      foot={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t('btn.cancel.label')}
          </button>
          <button type="button" className="btn primary" data-testid="master-assign-confirm" disabled={!chosen || busy} onClick={() => void assign()}>
            {t('templates.assign.confirm')}
          </button>
        </>
      }
    >
      {offered.length === 0 ? (
        <div role="status" data-testid="master-assign-none">
          {list.field
            ? t('templates.assign.none', { valueType: t(`master.type.${list.valueType}`), field: list.field })
            : t('templates.assign.nofield')}
        </div>
      ) : (
        <>
          <Field label={t('templates.col.template')} required hint={t('templates.assign.tip')}>
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              {offered.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.id} · {x.name}
                </option>
              ))}
            </select>
          </Field>
          {chosen && <TemplateSummary template={chosen} />}
        </>
      )}
      <Problem testid="master-assign-error" text={error} />
    </Dialog>
  );
}

function DeleteDialog({ list, onClose, onDeleted }: { list: MasterList; onClose: () => void; onDeleted: () => void }) {
  const { settings } = useShell();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.masters.remove(settings, list.id);
      onDeleted();
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };
  return (
    <Dialog
      busy={busy}
      title={t('master.delete.title', { id: list.id })}
      testid="master-delete-dialog"
      onClose={onClose}
      foot={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t('btn.cancel.label')}
          </button>
          <button type="button" className="btn danger" data-testid="master-delete-confirm" disabled={busy} onClick={() => void remove()}>
            {t('master.delete.action')}
          </button>
        </>
      }
    >
      <div>{t('master.delete.confirm', { name: list.name, n: list.itemCount.toLocaleString('en-US') })}</div>
      <Problem testid="master-delete-error" text={error} />
    </Dialog>
  );
}

// ---- templates ---------------------------------------------------------------------------------

function TemplatesTab() {
  const { settings, showToast } = useShell();
  const mayWrite = useMayWrite();
  const { templates, reloadTemplates, templateError } = useTemplates();
  const [lists, setLists] = useState<MasterList[]>([]);
  const [editing, setEditing] = useState<MasterTemplate | 'new' | null>(null);
  const [deleting, setDeleting] = useState<MasterTemplate | null>(null);
  /** Create from Excel: the draft the file gave, opened in the ordinary template form. */
  const [inferred, setInferred] = useState<{ file: string; result: MasterTemplateInferResult } | null>(null);
  const [inferring, setInferring] = useState(false);
  /** The template just created: its sample file is offered until another one is. */
  const [created, setCreated] = useState<string | null>(null);
  const inferInput = useRef<HTMLInputElement>(null);

  const infer = async (file: File) => {
    setInferring(true);
    try {
      setInferred({ file: file.name, result: await api.masters.templates.infer(settings, file) });
      setEditing('new');
    } catch (e) {
      showToast(errorText(e), 'error');
    } finally {
      setInferring(false);
      if (inferInput.current) inferInput.current.value = '';
    }
  };
  const sample = (id: string) => void downloadSample(settings, id, (m) => showToast(m, 'error'));

  const reloadLists = useCallback(async () => {
    try {
      const got = await api.masters.list(settings);
      setLists(Array.isArray(got) ? got : []);
    } catch {
      setLists([]);
    }
  }, [settings]);
  useEffect(() => {
    void reloadLists();
  }, [reloadLists]);

  const using = (id: string) => lists.filter((l) => l.templateId === id);
  const writable = (x: MasterTemplate) => mayWrite() && !isDemoTemplate(x);

  return (
    <>
      {templateError && (
        <div className="banner error" role="status" data-testid="template-error" style={{ marginBottom: 12 }}>
          {templateError}
        </div>
      )}
      <section className="card">
        <header className="card-head">
          <h2>{t('templates.tab.templates')}</h2>
          {templates && (
            <span className="chip mute" data-testid="template-count">
              {t('templates.count', { n: templates.length })}
            </span>
          )}
          <span className="spacer" />
          {mayWrite() && (
            <>
              <button
                type="button"
                className="btn small"
                data-testid="template-infer"
                disabled={inferring}
                title={t('templates.infer.tip')}
                onClick={() => inferInput.current?.click()}
              >
                {t('templates.infer.action')}
              </button>
              <input
                ref={inferInput}
                type="file"
                accept=".xlsx,.csv"
                hidden
                data-testid="template-infer-file"
                aria-label={t('templates.infer.action')}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void infer(file);
                }}
              />
              <button
                type="button"
                className="btn small"
                data-testid="template-new"
                onClick={() => {
                  setInferred(null);
                  setEditing('new');
                }}
              >
                {t('templates.new.action')}
              </button>
            </>
          )}
        </header>
        {created && (
          <div className="banner ok row-wrap" role="status" data-testid="template-created-sample" style={{ margin: '0 0 12px' }}>
            <span>{t('templates.created.sample', { id: created })}</span>
            <span className="spacer" />
            <button type="button" className="btn small" data-testid="template-created-sample-download" onClick={() => sample(created)}>
              {t('templates.sample.action')}
            </button>
            <button type="button" className="btn small" aria-label={t('btn.close.label')} onClick={() => setCreated(null)}>
              ✕
            </button>
          </div>
        )}
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 170 }}>{t('master.col.id')}</th>
                <th>{t('master.col.name')}</th>
                <th style={{ width: 90 }}>{t('templates.col.keyType')}</th>
                <th style={{ width: 150 }}>{t('templates.form.field')}</th>
                <th>{t('templates.columns.title')}</th>
                <th className="right" style={{ width: 70 }}>
                  {t('templates.col.lists')}
                </th>
                <th className="right" style={{ width: 70 }}>
                  {t('master.col.version')}
                </th>
                <th style={{ width: 90 }}>{t('templates.col.sample')}</th>
                <th style={{ width: 44 }} aria-label={t('master.col.actions')} />
              </tr>
            </thead>
            <tbody>
              {templates === null && (
                <tr>
                  <td colSpan={9} className="empty">
                    {t('master.loading')}
                  </td>
                </tr>
              )}
              {templates?.length === 0 && (
                <tr>
                  <td colSpan={9} className="empty" role="status" data-testid="template-empty">
                    {t('templates.empty')}
                  </td>
                </tr>
              )}
              {templates?.map((x) => (
                <tr
                  key={x.id}
                  className={writable(x) ? 'clickable' : undefined}
                  data-testid={`template-row-${x.id}`}
                  onClick={writable(x) ? () => setEditing(x) : undefined}
                >
                  <td className="mono">
                    {x.id}
                    {x.builtIn && (
                      <span className="chip info" style={{ marginLeft: 6 }}>
                        {t('templates.builtin.chip')}
                      </span>
                    )}
                    {isDemoTemplate(x) && (
                      <span className="chip warn" style={{ marginLeft: 6 }}>
                        {t('master.demo.chip')}
                      </span>
                    )}
                  </td>
                  <td>
                    {/* UX review P2-10: the row opens the template; its name is the keyboard way in. */}
                    <div>
                      {writable(x) ? (
                        <button
                          type="button"
                          className="rowlink"
                          data-testid={`template-open-${x.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditing(x);
                          }}
                        >
                          {x.name || x.id}
                        </button>
                      ) : (
                        x.name
                      )}
                    </div>
                    {x.description && <div className="small muted">{x.description}</div>}
                  </td>
                  <td className="small">{t(`master.type.${x.keyType}`)}</td>
                  <td className="mono small">{x.field}</td>
                  <td className="small" data-testid={`template-columns-${x.id}`}>
                    {x.columns.length === 0 ? '—' : x.columns.map((c) => `${c.label}${c.required ? ' *' : ''}`).join(', ')}
                  </td>
                  <td className="right" data-testid={`template-lists-${x.id}`}>
                    {using(x.id).length}
                  </td>
                  <td className="right">{x.version}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {/* Everyone who reads templates may take the sample; a demo copy has none on the server. */}
                    {!isDemoTemplate(x) && (
                      <button
                        type="button"
                        className="btn small"
                        data-testid={`template-sample-${x.id}`}
                        title={t('templates.sample.a11y', { id: x.id })}
                        aria-label={t('templates.sample.a11y', { id: x.id })}
                        onClick={() => sample(x.id)}
                      >
                        {t('templates.sample.short')}
                      </button>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {writable(x) && (
                      <ActionMenu
                        variant="row"
                        label={t('templates.row.menu.a11y', { id: x.id })}
                        items={[
                          { label: t('master.menu.edit'), onSelect: () => setEditing(x) },
                          { label: t('templates.sample.action'), onSelect: () => sample(x.id) },
                          { label: t('master.menu.delete'), onSelect: () => setDeleting(x), separated: true },
                        ]}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {editing && (
        <TemplateDialog
          initial={editing === 'new' ? undefined : editing}
          inferred={editing === 'new' ? (inferred ?? undefined) : undefined}
          inUse={editing === 'new' ? [] : using(editing.id).map((l) => l.id)}
          onClose={() => {
            setEditing(null);
            setInferred(null);
          }}
          onSaved={(x, isNew) => {
            setEditing(null);
            setInferred(null);
            if (isNew) setCreated(x.id);
            showToast(t(isNew ? 'templates.created.toast' : 'templates.saved.toast', { id: x.id }));
            void reloadTemplates();
          }}
        />
      )}
      {deleting && (
        <TemplateDeleteDialog
          template={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            showToast(t('templates.deleted.toast', { id: deleting.id }));
            setDeleting(null);
            void reloadTemplates();
          }}
        />
      )}
    </>
  );
}

const TEMPLATE_PROBLEM_KEY: Record<TemplateFormProblem, string> = {
  id: 'master.problem.id',
  name: 'master.problem.name',
  name_long: 'master.problem.name_long',
  description_long: 'master.problem.description_long',
  field: 'templates.problem.field',
  pattern: 'templates.problem.pattern',
  column_key: 'templates.problem.column_key',
  column_dup: 'templates.problem.column_dup',
  column_label: 'templates.problem.column_label',
  columns_many: 'templates.problem.columns_many',
};

/** One column's key problem, counted as `templateFormProblems` counts it: a bad or reserved key, or a key an earlier column has. */
function columnKeyProblem(columns: readonly MasterTemplateColumn[], i: number): TemplateFormProblem | undefined {
  const key = columns[i]!.key.trim();
  if (!MASTER_COLUMN_KEY_PATTERN.test(key) || key === 'value' || key === 'note') return 'column_key';
  if (columns.slice(0, i).some((c) => c.key.trim() === key)) return 'column_dup';
  return undefined;
}

/** New template or edit one, with its column editor. Key type and field are fixed while lists use it. */
function TemplateDialog({
  initial,
  inferred,
  inUse,
  onClose,
  onSaved,
}: {
  initial?: MasterTemplate;
  /** Create from Excel: the form starts from the file's draft; id, name and field are the user's. */
  inferred?: { file: string; result: MasterTemplateInferResult };
  inUse: string[];
  onClose: () => void;
  onSaved: (x: MasterTemplate, created: boolean) => void;
}) {
  const { settings } = useShell();
  const [form, setForm] = useState<TemplateForm>(() =>
    inferred
      ? {
          ...templateFormOf(),
          keyType: inferred.result.draft.keyType,
          keyPattern: inferred.result.draft.keyPattern ?? '',
          columns: inferred.result.draft.columns.map((c) => ({ ...c })),
        }
      : templateFormOf(initial),
  );
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const isNew = initial === undefined;
  const fixed = inUse.length > 0;
  const problems = templateFormProblems(form, isNew);
  // UX review P2-5: each problem under its own field, once that field was left or Save pressed.
  const errors = useFieldErrors();
  const problemText = (...codes: TemplateFormProblem[]) => {
    const hit = codes.find((c) => problems.includes(c));
    return hit ? t(TEMPLATE_PROBLEM_KEY[hit]) : undefined;
  };
  const patch = (p: Partial<TemplateForm>) => setForm((f) => ({ ...f, ...p }));
  const setColumn = (i: number, p: Partial<MasterTemplateColumn>) =>
    setForm((f) => ({ ...f, columns: f.columns.map((c, j) => (j === i ? { ...c, ...p } : c)) }));

  const save = async () => {
    if (!errors.attempt(problems.length === 0, document.querySelector('[data-testid="template-dialog"]'))) return;
    setBusy(true);
    setServerError(null);
    const body = {
      id: form.id.trim(),
      name: form.name.trim(),
      description: form.description.trim() || null,
      keyType: form.keyType,
      keyPattern: form.keyPattern || null,
      field: form.field as keyof NormalizedTxn,
      columns: form.columns.map((c) => ({ key: c.key.trim(), label: c.label.trim() || c.key.trim(), type: c.type, required: c.required })),
    };
    try {
      const saved = isNew ? await api.masters.templates.create(settings, body) : await api.masters.templates.update(settings, initial.id, initial._etag, body);
      onSaved(saved, isNew);
    } catch (e) {
      setServerError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      busy={busy}
      wide
      title={
        inferred ? t('templates.infer.title', { file: inferred.file }) : isNew ? t('templates.create.title') : t('templates.edit.title', { id: initial.id })
      }
      testid="template-dialog"
      onClose={onClose}
      foot={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t('btn.cancel.label')}
          </button>
          <button type="button" className="btn primary" data-testid="template-save" disabled={busy} onClick={() => void save()}>
            {isNew ? t('master.create.action') : t('master.save.action')}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field
          label={t('master.form.id')}
          required={isNew}
          hint={isNew ? t('master.form.id.tip') : undefined}
          name="id"
          form={errors}
          error={errors.show('id', problemText('id'))}
        >
          <input
            type="text"
            className="mono-input"
            value={form.id}
            disabled={!isNew}
            spellCheck={false}
            onChange={(e) => patch({ id: e.target.value.toUpperCase() })}
          />
        </Field>
        <Field label={t('master.form.name')} required name="name" form={errors} error={errors.show('name', problemText('name', 'name_long'))}>
          <input type="text" value={form.name} onChange={(e) => patch({ name: e.target.value })} />
        </Field>
      </div>
      <Field label={t('master.form.description')} name="description" form={errors} error={errors.show('description', problemText('description_long'))}>
        <input type="text" value={form.description} onChange={(e) => patch({ description: e.target.value })} />
      </Field>
      <div className="form-grid">
        <Field
          label={t('templates.col.keyType')}
          required={!fixed}
          hint={fixed ? t('templates.form.fixed.tip', { lists: inUse.join(', ') }) : undefined}
        >
          <select
            data-testid="template-keytype"
            value={form.keyType}
            disabled={fixed}
            onChange={(e) => {
              const keyType = e.target.value as TemplateForm['keyType'];
              const keep = form.field !== '' && (fieldsFor(keyType) as string[]).includes(form.field);
              patch({ keyType, field: keep ? form.field : '' });
            }}
          >
            <option value="string">{t('master.type.string')}</option>
            <option value="number">{t('master.type.number')}</option>
          </select>
        </Field>
        <Field
          label={t('templates.form.field')}
          required={!fixed}
          hint={t('templates.form.field.tip')}
          name="field"
          form={errors}
          error={errors.show('field', problemText('field'))}
        >
          <select data-testid="template-field" value={form.field} disabled={fixed} onChange={(e) => patch({ field: e.target.value as keyof NormalizedTxn | '' })}>
            <option value="">{t('templates.form.field.choose')}</option>
            {fieldsFor(form.keyType).map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label={t('templates.form.keyPattern')}
          hint={t('templates.form.keyPattern.tip')}
          name="pattern"
          form={errors}
          error={errors.show('pattern', problemText('pattern'))}
        >
          <input type="text" className="mono-input" value={form.keyPattern} spellCheck={false} onChange={(e) => patch({ keyPattern: e.target.value })} />
        </Field>
      </div>

      {inferred && <InferPreview file={inferred.file} result={inferred.result} />}

      <div className="stack" data-testid="template-columns">
        <div className="row-wrap">
          <strong className="small">{t('templates.columns.title')}</strong>
          <Tip>{t('templates.column.key.tip')}</Tip>
          <span className="spacer" />
          <button
            type="button"
            className="btn small"
            data-testid="template-column-add"
            onClick={() => patch({ columns: [...form.columns, { key: '', label: '', type: 'string', required: false }] })}
          >
            {t('templates.columns.add')}
          </button>
        </div>
        {form.columns.length === 0 && <div className="small muted">{t('templates.columns.empty')}</div>}
        {form.columns.map((c, i) => {
          const keyProblem = columnKeyProblem(form.columns, i);
          const labelLong = c.label.trim().length > TEMPLATE_MAX_LABEL;
          // The visible labels repeat on every row, so each control keeps its own "Column n …" name.
          return (
            <div key={i} className="row-wrap" style={{ alignItems: 'flex-start' }} data-testid={`template-column-${i}`}>
              <Field
                label={t('templates.column.key')}
                required
                name={`column-${i}-key`}
                form={errors}
                error={errors.show(`column-${i}-key`, keyProblem && t(TEMPLATE_PROBLEM_KEY[keyProblem]))}
              >
                <input
                  type="text"
                  className="mono-input"
                  style={{ width: 150 }}
                  aria-label={t('templates.column.key.a11y', { n: i + 1 })}
                  value={c.key}
                  spellCheck={false}
                  onChange={(e) => setColumn(i, { key: e.target.value.toLowerCase() })}
                />
              </Field>
              <Field
                label={t('templates.column.label')}
                name={`column-${i}-label`}
                form={errors}
                error={errors.show(`column-${i}-label`, labelLong && t('templates.problem.column_label'))}
              >
                <input
                  type="text"
                  style={{ width: 180 }}
                  aria-label={t('templates.column.label.a11y', { n: i + 1 })}
                  value={c.label}
                  onChange={(e) => setColumn(i, { label: e.target.value })}
                />
              </Field>
              <Field label={t('templates.column.type')}>
                <select
                  aria-label={t('templates.column.type.a11y', { n: i + 1 })}
                  value={c.type}
                  onChange={(e) => setColumn(i, { type: e.target.value as MasterTemplateColumn['type'] })}
                >
                  {COLUMN_TYPES.map((ty) => (
                    <option key={ty} value={ty}>
                      {t(`templates.type.${ty}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t('templates.column.required')}>
                <input type="checkbox" checked={c.required} onChange={(e) => setColumn(i, { required: e.target.checked })} />
              </Field>
              <div className="field">
                {/* An empty head, so the button lines up with the controls rather than with the labels. */}
                <span className="field-head" aria-hidden="true">
                  {' '}
                </span>
                <button
                  type="button"
                  className="btn small danger"
                  aria-label={t('templates.column.remove.a11y', { n: i + 1 })}
                  onClick={() => patch({ columns: form.columns.filter((_, j) => j !== i) })}
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}
        {/* Too many columns belongs to no one column: it shows under the columns. */}
        {errors.show('columns', problems.includes('columns_many')) && (
          <span className="field-error" data-testid="template-columns-error">
            {t('templates.problem.columns_many')}
          </span>
        )}
      </div>
      <Problem testid="template-form-error" text={serverError} />
    </Dialog>
  );
}

/**
 * Create from Excel: what the file holds, as read — the headers, the type and "required" read
 * from the rows under them, the first rows, and anything about the header row worth fixing.
 */
function InferPreview({ file, result }: { file: string; result: MasterTemplateInferResult }) {
  const typeOf = (c: MasterTemplateColumn) => `${t(`templates.type.${c.type}`)}${c.required ? ` · ${t('templates.column.required')}` : ''}`;
  return (
    <div className="stack" data-testid="template-infer-preview">
      <div className="small muted" data-testid="template-infer-summary">
        {t('templates.infer.summary', { file, sampled: result.sampled.toLocaleString('en-US'), rows: result.rows.toLocaleString('en-US') })}
      </div>
      {result.warnings.length > 0 && (
        <ul className="small" data-testid="template-infer-warnings" style={{ margin: 0, paddingLeft: 18 }}>
          {result.warnings.map((w, i) => (
            <li key={i}>{t(`templates.infer.warn.${w.code}`, { column: String(w.column ?? ''), header: w.header ?? '' })}</li>
          ))}
        </ul>
      )}
      <div className="tbl-wrap" style={{ maxHeight: 220, overflow: 'auto' }}>
        <table data-testid="template-infer-sample">
          <thead>
            <tr>
              <th>
                <div>{result.valueHeader || t('master.col.value')}</div>
                <div className="small muted">
                  {t('master.col.value')} · {t(`master.type.${result.draft.keyType}`)}
                </div>
              </th>
              {result.noteHeader !== null && (
                <th>
                  <div>{result.noteHeader}</div>
                  <div className="small muted">{t('master.col.note')}</div>
                </th>
              )}
              {result.draft.columns.map((c, i) => (
                <th key={c.key} data-testid={`template-infer-col-${c.key}`}>
                  <div>{result.headers[i]?.header || c.label}</div>
                  <div className="small muted">{typeOf(c)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.sample.map((r) => (
              <tr key={r.row}>
                <td className="mono">{r.value}</td>
                {result.noteHeader !== null && <td className="small">{r.note ?? ''}</td>}
                {r.cells.map((cell, i) => (
                  <td key={i} className="small">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TemplateDeleteDialog({ template, onClose, onDeleted }: { template: MasterTemplate; onClose: () => void; onDeleted: () => void }) {
  const { settings } = useShell();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.masters.templates.remove(settings, template.id, template._etag);
      onDeleted();
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };
  return (
    <Dialog
      busy={busy}
      title={t('templates.delete.title', { id: template.id })}
      testid="template-delete-dialog"
      onClose={onClose}
      foot={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t('btn.cancel.label')}
          </button>
          <button type="button" className="btn danger" data-testid="template-delete-confirm" disabled={busy} onClick={() => void remove()}>
            {t('master.delete.action')}
          </button>
        </>
      }
    >
      <div>{t('templates.delete.confirm', { name: template.name })}</div>
      <Problem testid="template-delete-error" text={error} />
    </Dialog>
  );
}

// ---- one list ------------------------------------------------------------------------------

const PAGE = 100;

/** One column's input: text, a number, a date, or a yes/no choice. */
function ColumnInput({
  column,
  value,
  onChange,
  labelled = false,
  ...wiring
}: {
  column: MasterTemplateColumn;
  value: string;
  onChange: (v: string) => void;
  /** Inside a `<Field>`: the field's label names it, and passes its id, aria and blur (`wiring`) down here. */
  labelled?: boolean;
  id?: string;
  className?: string;
  onBlur?: (e: FocusEvent<HTMLElement>) => void;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-required'?: boolean | 'true' | 'false';
}) {
  // In the inline add row there is no label above the control: its name and placeholder carry it.
  const name = labelled ? undefined : column.label;
  const placeholder = labelled ? undefined : column.required ? `${column.label} *` : column.label;
  if (column.type === 'boolean') {
    return (
      <select {...wiring} aria-label={name} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{placeholder ?? '—'}</option>
        <option value="true">{t('templates.bool.true')}</option>
        <option value="false">{t('templates.bool.false')}</option>
      </select>
    );
  }
  return (
    <input
      {...wiring}
      type={column.type === 'date' ? 'date' : 'text'}
      inputMode={column.type === 'number' ? 'decimal' : undefined}
      aria-label={name}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function attrProblemText(template: MasterTemplate, column: string, reason: string): string {
  const label = template.columns.find((c) => c.key === column)?.label ?? column;
  return t('templates.attr.invalid', { column: label, reason: t(`templates.reason.${reason}`) });
}

interface ItemDraft {
  value: string;
  note: string;
  attrs: Record<string, string>;
}

function MasterDetail({ id }: { id: string }) {
  const { settings, showToast } = useShell();
  const mayWrite = useMayWrite()(id);
  const { templates } = useTemplates();
  const [list, setList] = useState<MasterList | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<MasterItem[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState<ItemDraft | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<MasterItem | null>(null);
  const [editingItem, setEditingItem] = useState<MasterItem | null>(null);
  const [importing, setImporting] = useState(false);
  const [assigning, setAssigning] = useState(false);
  /** Bumped per first-page load, so a slow answer to an older search cannot overwrite a newer one. */
  const query = useRef(0);
  const template = list?.templateId ? (templates?.find((x) => x.id === list.templateId) ?? null) : null;
  const columns = template?.columns ?? [];

  const loadHeader = useCallback(async () => {
    try {
      setList(await api.masters.get(settings, id));
      setMissing(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setMissing(true);
      else setError(errorText(e));
    }
  }, [settings, id]);

  const loadFirst = useCallback(
    async (needle: string) => {
      const mine = ++query.current;
      try {
        const page = await api.masters.items(settings, id, { search: needle || undefined, limit: PAGE });
        if (mine !== query.current) return;
        setItems(page.items);
        setCursor(page.nextCursor);
      } catch (e) {
        if (mine === query.current && !(e instanceof ApiError && e.status === 404)) setError(errorText(e));
      }
    },
    [settings, id],
  );

  const refresh = useCallback(async () => {
    await Promise.all([loadHeader(), loadFirst(search)]);
  }, [loadHeader, loadFirst, search]);

  useEffect(() => {
    void loadHeader();
  }, [loadHeader]);

  useEffect(() => {
    const timer = setTimeout(() => void loadFirst(search), search === '' ? 0 : 250);
    return () => clearTimeout(timer);
  }, [loadFirst, search]);

  const loadMore = async () => {
    if (!cursor) return;
    const mine = query.current;
    try {
      const page = await api.masters.items(settings, id, { search: search || undefined, limit: PAGE, cursor });
      if (mine !== query.current) return;
      setItems((prev) => [...prev, ...page.items]);
      setCursor(page.nextCursor);
    } catch (e) {
      setError(errorText(e));
    }
  };

  const add = async () => {
    if (!adding || !list) return;
    const checked = canonicalMasterValue(list.valueType, adding.value);
    if (!checked.ok) {
      setAddError(t('master.value.invalid', { reason: t(`master.reason.${checked.reason}`) }));
      return;
    }
    if (template && !keyMatches(template, checked.value)) {
      setAddError(t('master.value.invalid', { reason: t('templates.reason.key_pattern') }));
      return;
    }
    let attrs: Record<string, MasterAttrValue> | undefined;
    if (template) {
      const parsed = parseAttrs(template, adding.attrs);
      if (!parsed.ok) {
        setAddError(attrProblemText(template, parsed.column, parsed.reason));
        return;
      }
      attrs = parsed.attrs;
    }
    try {
      const res = await api.masters.addItem(settings, id, checked.value, adding.note.trim() || undefined, attrs);
      setAdding(null);
      setAddError(null);
      showToast(
        res.added ? t('master.item.added.toast', { value: res.item.value }) : t('master.item.exists.toast', { value: res.item.value }),
      );
      await refresh();
    } catch (e) {
      setAddError(errorText(e));
    }
  };

  const remove = async (item: MasterItem) => {
    try {
      await api.masters.removeItem(settings, id, item.value);
      setRemoving(null);
      showToast(t('master.item.removed.toast', { value: item.value }));
      await refresh();
    } catch (e) {
      setRemoving(null);
      showToast(errorText(e), 'error');
    }
  };

  if (missing) {
    return (
      <>
        <Link to="/masters" className="small">
          {t('master.back')}
        </Link>
        <PageHead title={t('master.title')} />
        <div className="empty" role="status" data-testid="master-notfound">
          {t('master.notfound')}
        </div>
      </>
    );
  }

  const colCount = (mayWrite ? 4 : 3) + columns.length;

  return (
    <>
      <Link to="/masters" className="small" data-testid="master-back">
        {t('master.back')}
      </Link>
      <PageHead
        title={list?.name ?? id}
        chips={
          <>
            <span className="chip mono" style={{ marginLeft: 8 }}>
              {id}
            </span>
            {isDemoMaster(id) && <span className="chip warn">{t('master.demo.chip')}</span>}
          </>
        }
      />

      {error && (
        <div className="banner error" role="status" data-testid="master-error" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}

      {list && (
        <section className="card" data-testid="master-summary">
          <div className="kv">
            <Kv k={t('templates.col.template')} v={list.templateId ?? t('templates.none.chip')} mono={!!list.templateId} clip testid="master-summary-template" />
            <Kv k={t('master.form.valueType')} v={t(`master.type.${list.valueType}`)} />
            <Kv k={t('master.form.field')} v={list.field ?? '—'} mono clip />
            {template?.keyPattern && <Kv k={t('templates.form.keyPattern')} v={template.keyPattern} mono />}
            <Kv k={t('master.col.items')} v={list.itemCount.toLocaleString('en-US')} testid="master-summary-count" />
            <Kv k={t('master.col.version')} v={String(list.version)} clip testid="master-summary-version" />
            {/* When and who on separate rows, each on one line (UX review P1-1). */}
            <Kv k={t('master.col.updated')} v={<When iso={list.updatedUtc} />} clip testid="master-summary-updated" />
            <Kv k={t('master.summary.updated_by')} v={<span title={list.updatedBy}>{actorLabel(list.updatedBy)}</span>} clip testid="master-summary-updated-by" />
            <Kv k={t('master.summary.created')} v={<When iso={list.createdUtc} />} clip testid="master-summary-created" />
            <Kv k={t('master.summary.created_by')} v={<span title={list.createdBy}>{actorLabel(list.createdBy)}</span>} clip testid="master-summary-created-by" />
            {list.description && <Kv k={t('master.form.description')} v={list.description} />}
          </div>
          {mayWrite && !list.templateId && (
            <div style={{ marginTop: 10 }}>
              <button type="button" className="btn small" data-testid="master-assign" onClick={() => setAssigning(true)}>
                {t('templates.assign.action')}
              </button>
            </div>
          )}
        </section>
      )}

      <section className="card">
        <header className="card-head">
          <h2>{t('master.items.title')}</h2>
          {list && (
            <span className="chip mute" data-testid="master-shown">
              {t('master.items.shown', { shown: items.length.toLocaleString('en-US'), n: list.itemCount.toLocaleString('en-US') })}
            </span>
          )}
          <span className="spacer" />
          <input
            type="search"
            className="small"
            style={{ width: 220 }}
            value={search}
            placeholder={t('master.items.search.placeholder')}
            aria-label={t('master.items.search.a11y')}
            data-testid="master-search"
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className="btn small"
            data-testid="master-export"
            onClick={() => void exportList(settings, id, (m) => showToast(m, 'error'))}
          >
            {t('master.export.action')}
          </button>
          {!isDemoMaster(id) && (
            <button
              type="button"
              className="btn small"
              data-testid="master-export-xlsx"
              onClick={() => void exportList(settings, id, (m) => showToast(m, 'error'), 'xlsx')}
            >
              {t('master.export.xlsx')}
            </button>
          )}
          {mayWrite && list && (
            <>
              <button type="button" className="btn small" data-testid="master-import" onClick={() => setImporting(true)}>
                {t('master.import.action')}
              </button>
              <button
                type="button"
                className="btn small"
                data-testid="master-add"
                onClick={() => {
                  setAdding({ value: '', note: '', attrs: {} });
                  setAddError(null);
                }}
              >
                {t('master.item.add.action')}
              </button>
            </>
          )}
        </header>

        {adding && (
          <form
            className="row-wrap"
            data-testid="master-add-form"
            style={{ marginBottom: 10 }}
            onSubmit={(e) => {
              e.preventDefault();
              void add();
            }}
          >
            <input
              type="text"
              className="mono-input"
              aria-label={t('master.item.value.a11y')}
              placeholder={t('master.col.value')}
              value={adding.value}
              onChange={(e) => setAdding({ ...adding, value: e.target.value })}
            />
            <input
              type="text"
              aria-label={t('master.col.note')}
              placeholder={t('master.col.note')}
              value={adding.note}
              onChange={(e) => setAdding({ ...adding, note: e.target.value })}
            />
            {columns.map((c) => (
              <ColumnInput
                key={c.key}
                column={c}
                value={adding.attrs[c.key] ?? ''}
                onChange={(v) => setAdding({ ...adding, attrs: { ...adding.attrs, [c.key]: v } })}
              />
            ))}
            <button type="submit" className="btn small primary" data-testid="master-add-confirm" disabled={adding.value.trim() === ''}>
              {t('master.item.add.confirm')}
            </button>
            <button type="button" className="btn small" onClick={() => setAdding(null)}>
              {t('btn.cancel.label')}
            </button>
            <Problem testid="master-add-error" text={addError} />
          </form>
        )}

        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('master.col.value')}</th>
                <th>{t('master.col.note')}</th>
                {columns.map((c) => (
                  <th key={c.key} data-testid={`master-col-${c.key}`}>
                    {c.label}
                  </th>
                ))}
                <th style={{ width: 260 }}>{t('master.col.added')}</th>
                {mayWrite && <th style={{ width: 44 }} aria-label={t('master.col.actions')} />}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={colCount} className="empty" role="status" data-testid="master-items-empty">
                    {t('master.items.empty')}
                  </td>
                </tr>
              )}
              {items.map((i) => (
                // UX review P2-10: a value opens for editing from its row (or its value, the keyboard
                // way in); Edit and Remove sit in the row's ••• menu, not as buttons on every row.
                <tr
                  key={i.value}
                  className={mayWrite ? 'clickable' : undefined}
                  data-testid={`master-item-${i.value}`}
                  onClick={mayWrite ? () => setEditingItem(i) : undefined}
                >
                  <td className="mono">
                    {mayWrite ? (
                      <button
                        type="button"
                        className="rowlink"
                        data-testid={`master-value-open-${i.value}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingItem(i);
                        }}
                      >
                        {i.value}
                      </button>
                    ) : (
                      i.value
                    )}
                  </td>
                  <td className="small">{i.note ?? ''}</td>
                  {columns.map((c) => (
                    <td key={c.key} className="small" data-testid={`master-attr-${i.value}-${c.key}`}>
                      {attrText(i.attrs?.[c.key])}
                    </td>
                  ))}
                  <td className="small">
                    <When iso={i.addedUtc} />{' '}
                    <span className="muted" title={i.addedBy}>
                      · {actorLabel(i.addedBy)}
                    </span>
                  </td>
                  {mayWrite && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <ActionMenu
                        variant="row"
                        label={t('master.row.menu.a11y', { id: i.value })}
                        items={[
                          { label: t('master.menu.edit'), onSelect: () => setEditingItem(i) },
                          { label: t('master.item.remove.action'), onSelect: () => setRemoving(i), separated: true },
                        ]}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {cursor && (
          <div style={{ marginTop: 10 }}>
            <button type="button" className="btn small" data-testid="master-more" onClick={() => void loadMore()}>
              {t('master.items.more')}
            </button>
          </div>
        )}
      </section>

      {removing && (
        <Dialog
          title={t('master.item.remove.title', { value: removing.value })}
          testid="master-remove-dialog"
          onClose={() => setRemoving(null)}
          foot={
            <>
              <span className="spacer" />
              <button type="button" className="btn" onClick={() => setRemoving(null)}>
                {t('btn.cancel.label')}
              </button>
              <button type="button" className="btn danger" data-testid="master-remove-confirm" onClick={() => void remove(removing)}>
                {t('master.item.remove.action')}
              </button>
            </>
          }
        >
          <div className="mono">{removing.value}</div>
        </Dialog>
      )}

      {editingItem && list && (
        <ItemDialog
          listId={id}
          item={editingItem}
          template={template}
          onClose={() => setEditingItem(null)}
          onSaved={(updated) => {
            setEditingItem(null);
            showToast(t(updated ? 'templates.item.saved.toast' : 'templates.item.unchanged.toast', { value: editingItem.value }));
            void refresh();
          }}
        />
      )}

      {assigning && list && (
        <AssignDialog
          list={list}
          templates={templates ?? []}
          onClose={() => setAssigning(false)}
          onDone={(l) => {
            setAssigning(false);
            showToast(t('templates.assigned.toast', { id: l.id, template: l.templateId ?? '' }));
            void refresh();
          }}
        />
      )}

      {importing && list && (
        <ImportDialog
          list={list}
          template={template}
          onClose={() => setImporting(false)}
          onDone={(r) => {
            setImporting(false);
            const counts = { id, added: r.added.toLocaleString('en-US'), removed: r.removed.toLocaleString('en-US') };
            // QA-A-02: values already in the list that took the file's note or columns are counted too.
            showToast(
              (r.updated ?? 0) > 0
                ? t('master.import.done.updated.toast', { ...counts, updated: (r.updated ?? 0).toLocaleString('en-US') })
                : t('master.import.done.toast', counts),
            );
            void refresh();
          }}
        />
      )}
    </>
  );
}

/** Edit one value's note and template columns (the value itself is the key: remove and add to change it). */
function ItemDialog({
  listId,
  item,
  template,
  onClose,
  onSaved,
}: {
  listId: string;
  item: MasterItem;
  template: MasterTemplate | null;
  onClose: () => void;
  onSaved: (updated: boolean) => void;
}) {
  const { settings } = useShell();
  const [note, setNote] = useState(item.note ?? '');
  const [attrs, setAttrs] = useState<Record<string, string>>(() =>
    Object.fromEntries((template?.columns ?? []).map((c) => [c.key, attrInput(item.attrs?.[c.key])])),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // UX review P2-5: each column's problem under that column, once it was left or Save pressed.
  const errors = useFieldErrors();
  const columnProblem = (c: MasterTemplateColumn): string | undefined => {
    const r = parseAttr(c, attrs[c.key] ?? '');
    return r.ok ? undefined : t(`templates.reason.${r.reason}`);
  };
  const save = async () => {
    let parsed: Record<string, MasterAttrValue> = {};
    if (template) {
      const r = parseAttrs(template, attrs);
      errors.attempt(r.ok, document.querySelector('[data-testid="master-item-dialog"]'));
      if (!r.ok) return;
      parsed = r.attrs;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.masters.updateItem(settings, listId, item.value, note.trim() || undefined, parsed);
      onSaved(res.updated);
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };
  return (
    <Dialog
      busy={busy}
      title={t('templates.item.edit.title', { value: item.value })}
      testid="master-item-dialog"
      onClose={onClose}
      foot={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t('btn.cancel.label')}
          </button>
          <button type="button" className="btn primary" data-testid="master-item-save" disabled={busy} onClick={() => void save()}>
            {t('master.save.action')}
          </button>
        </>
      }
    >
      <Field label={t('master.col.note')}>
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      {(template?.columns ?? []).map((c) => (
        <Field
          key={c.key}
          label={c.label}
          required={c.required}
          name={`attr-${c.key}`}
          form={errors}
          error={errors.show(`attr-${c.key}`, columnProblem(c))}
        >
          <ColumnInput labelled column={c} value={attrs[c.key] ?? ''} onChange={(v) => setAttrs((a) => ({ ...a, [c.key]: v }))} />
        </Field>
      ))}
      <Problem testid="master-item-error" text={error} />
    </Dialog>
  );
}

// ---- import --------------------------------------------------------------------------------

/** A reason code as text: the value rules (`master.reason.*`) or a template rule (`templates.reason.*`). */
function reasonText(reason: string): string {
  return reason.startsWith('attr_') || reason === 'key_pattern' ? t(`templates.reason.${reason}`) : t(`master.reason.${reason}`);
}

/** File → dry-run preview (added / removed / updated / unchanged / invalid, duplicates) → confirm. Nothing changes before Confirm. */
function ImportDialog({
  list,
  template,
  onClose,
  onDone,
}: {
  list: MasterList;
  template: MasterTemplate | null;
  onClose: () => void;
  onDone: (r: MasterImportResult) => void;
}) {
  const { settings, showToast } = useShell();
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<MasterImportMode>('append');
  const [preview, setPreview] = useState<MasterImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const columns = template?.columns ?? [];
  const labelOf = (key: string) => columns.find((c) => c.key === key)?.label ?? key;

  // UX review P2-5: Preview with no file says so under the file field instead of being greyed out.
  const errors = useFieldErrors();
  const run = async (dryRun: boolean) => {
    if (!errors.attempt(file !== null, document.querySelector('[data-testid="master-import-dialog"]')) || !file) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.masters.importFile(settings, list.id, file, mode, dryRun);
      if (dryRun) setPreview(res);
      else onDone(res);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const blocked = preview !== null && preview.invalidCount > 0;

  return (
    <Dialog
      busy={busy}
      wide={columns.length > 0}
      title={t('master.import.title', { id: list.id })}
      testid="master-import-dialog"
      onClose={onClose}
      foot={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t('btn.cancel.label')}
          </button>
          {preview === null ? (
            <button
              type="button"
              className="btn primary"
              data-testid="master-import-preview"
              disabled={busy}
              onClick={() => void run(true)}
            >
              {t('master.import.preview')}
            </button>
          ) : (
            <button
              type="button"
              className="btn primary"
              data-testid="master-import-confirm"
              disabled={blocked || busy}
              onClick={() => void run(false)}
            >
              {t('master.import.confirm')}
            </button>
          )}
        </>
      }
    >
      {/* A list made from a template: its sample file is the easy way to get the header row right. */}
      {template && (
        <div className="row-wrap small" data-testid="master-import-sample-offer">
          <span className="muted">{t('templates.import.sample.lead')}</span>
          <button
            type="button"
            className="btn small"
            data-testid="master-import-sample-download"
            onClick={() => void downloadSample(settings, template.id, (m) => showToast(m, 'error'))}
          >
            {t('templates.sample.action')}
          </button>
        </div>
      )}
      <Field
        label={t('master.import.file')}
        required
        hint={
          template ? (
            <>
              {t('templates.import.sample.tip')}
              {columns.length > 0 && <> {t('templates.import.columns.tip', { columns: columns.map((c) => c.key).join(', ') })}</>}
            </>
          ) : (
            t('master.import.format.tip')
          )
        }
        name="file"
        form={errors}
        error={errors.show('file', file === null && t('master.import.problem.file'))}
      >
        <input
          type="file"
          accept=".xlsx,.csv"
          data-testid="master-import-file"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setPreview(null);
          }}
        />
      </Field>
      <FieldGroup
        legend={t('master.import.mode')}
        required
        hint={columns.length > 0 ? t('templates.import.existing.tip') : undefined}
        className="row-wrap"
      >
        {(['append', 'replace'] as const).map((m) => (
          <label key={m} className="row-wrap small">
            <input
              type="radio"
              name="master-import-mode"
              value={m}
              checked={mode === m}
              onChange={() => {
                setMode(m);
                setPreview(null);
              }}
            />
            {t(`master.import.mode.${m}`)}
          </label>
        ))}
      </FieldGroup>

      {preview && (
        <div className="stack" data-testid="master-import-result">
          <div className="tiles">
            <Tile label={t('master.import.total')} value={preview.total} testid="master-import-total" />
            <Tile label={t('master.import.added')} value={preview.added} testid="master-import-added" />
            <Tile label={t('master.import.removed')} value={preview.removed} testid="master-import-removed" />
            {/* QA-A-02: values already in the list take the file's note and columns. */}
            <Tile label={t('master.import.updated')} value={preview.updated ?? 0} testid="master-import-updated" />
            <Tile label={t('master.import.unchanged')} value={preview.unchanged} testid="master-import-unchanged" />
            <Tile label={t('master.import.invalid')} value={preview.invalidCount} testid="master-import-invalid" />
          </div>
          {(preview.duplicates ?? 0) > 0 && (
            <div className="small muted" data-testid="master-import-duplicates">
              {t('master.import.duplicates', { n: (preview.duplicates ?? 0).toLocaleString('en-US') })}
            </div>
          )}
          {preview.columns && preview.columns.length > 0 && (
            <div className="row-wrap small" data-testid="master-import-columns">
              <span className="muted">{t('templates.import.columns')}</span>
              {preview.columns.map((c) => (
                <span key={c.key} className={c.found ? 'chip ok' : 'chip mute'} title={c.found ? undefined : t('templates.import.missing')}>
                  {c.label}
                </span>
              ))}
            </div>
          )}
          {preview.ignoredColumns && preview.ignoredColumns.length > 0 && (
            <div className="small muted" data-testid="master-import-ignored">
              {t('templates.import.ignored', { columns: preview.ignoredColumns.join(', ') })}
            </div>
          )}
          {preview.sample && preview.sample.length > 0 && columns.length > 0 && (
            <div className="tbl-wrap" style={{ maxHeight: 200, overflow: 'auto' }}>
              <table data-testid="master-import-sample">
                <thead>
                  <tr>
                    <th>{t('master.col.value')}</th>
                    <th>{t('master.col.note')}</th>
                    {columns.map((c) => (
                      <th key={c.key}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.map((r) => (
                    <tr key={r.value}>
                      <td className="mono">{r.value}</td>
                      <td className="small">{r.note ?? ''}</td>
                      {columns.map((c) => (
                        <td key={c.key} className="small">
                          {attrText(r.attrs?.[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview.invalid.length > 0 && (
            <>
              <div className="tbl-wrap" style={{ maxHeight: 220, overflow: 'auto' }}>
                <table data-testid="master-import-invalid-rows">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>{t('master.col.row')}</th>
                      <th>{t('master.col.value')}</th>
                      {columns.length > 0 && <th>{t('templates.col.column')}</th>}
                      <th>{t('master.col.reason')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.invalid.map((r) => (
                      <tr key={r.row}>
                        <td>{r.row}</td>
                        <td className="mono">{r.value}</td>
                        {columns.length > 0 && <td className="small">{r.column ? labelOf(r.column) : ''}</td>}
                        <td className="small">{reasonText(r.reason)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.invalidCount > preview.invalid.length && (
                <div className="small muted">{t('master.import.invalid.more', { n: preview.invalidCount.toLocaleString('en-US') })}</div>
              )}
              <Problem testid="master-import-blocked" text={t('master.import.blocked')} />
            </>
          )}
        </div>
      )}
      <Problem testid="master-import-error" text={error} />
    </Dialog>
  );
}

function Tile({ label, value, testid }: { label: string; value: number; testid: string }) {
  return (
    <div className="tile">
      <div className="label">{label}</div>
      <div className="value" data-testid={testid}>
        {value.toLocaleString('en-US')}
      </div>
    </div>
  );
}
